// The notifications list hook powering both the dropdown preview and the full
// screen. Owns fetching, cursor pagination, client-side timeframe filtering,
// and the mark-read actions. Components stay presentational and just call this.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import i18n from "i18next";
import { useApiWithAuth } from "../../hooks/useAuth.js";
import {
  getNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from "./api.js";
import {
  isUnread,
  type NotificationItem,
  type NotificationTimeframe,
  type NotificationType,
} from "./model.js";
import { filterByTimeframe } from "./timeframe.js";
import { isMockNotificationsEnabled } from "./mockConfig.js";
import { useNotificationsContext } from "./NotificationsProvider.js";

interface UseNotificationsParams {
  /** Client-side type filter; empty means all types. */
  types?: NotificationType[];
  timeframe?: NotificationTimeframe;
  pageSize?: number;
  /** When false the hook doesn't fetch (e.g. dropdown closed). */
  enabled?: boolean;
}

interface UseNotificationsResult {
  items: NotificationItem[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  error: string | null;
  loadMore: () => void;
  refresh: () => Promise<void>;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
}

export function useNotifications({
  types = [],
  timeframe = "all",
  pageSize = 20,
  enabled = true,
}: UseNotificationsParams = {}): UseNotificationsResult {
  const { apiCall, isAuthenticated } = useApiWithAuth();
  const { decrementUnread, resetUnread, refreshUnreadCount } =
    useNotificationsContext();

  // useApiWithAuth returns a fresh apiCall each render; hold it in a ref so
  // load/markRead stay stable and their effects don't loop.
  const apiCallRef = useRef(apiCall);
  apiCallRef.current = apiCall;

  const [items, setItems] = useState<NotificationItem[]>([]);
  const [nextBefore, setNextBefore] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (before: string | null) => {
      if (!isAuthenticated && !isMockNotificationsEnabled()) {
        setItems([]);
        setHasMore(false);
        return;
      }
      const isFirstPage = before === null;
      if (isFirstPage) setIsLoading(true);
      else setIsLoadingMore(true);
      setError(null);
      try {
        // Fetch unfiltered; type + timeframe filtering happen client-side over
        // the loaded pages (keeps multi-type filtering + coalescing simple).
        const page = await getNotifications(apiCallRef.current, {
          before,
          limit: pageSize,
        });
        setItems((prev) =>
          isFirstPage ? page.items : [...prev, ...page.items],
        );
        setNextBefore(page.nextBefore);
        setHasMore(page.nextBefore !== null);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : i18n.t("features:notifications.loadFailed"),
        );
      } finally {
        if (isFirstPage) setIsLoading(false);
        else setIsLoadingMore(false);
      }
    },
    [isAuthenticated, pageSize],
  );

  // Reload from scratch whenever the type filter or enabled state changes.
  useEffect(() => {
    if (!enabled) return;
    void load(null);
  }, [enabled, load]);

  const loadMore = useCallback(() => {
    if (isLoadingMore || !hasMore || nextBefore === null) return;
    void load(nextBefore);
  }, [isLoadingMore, hasMore, nextBefore, load]);

  const refresh = useCallback(() => load(null), [load]);

  const markRead = useCallback(
    async (id: string) => {
      const target = items.find((n) => n.id === id);
      if (!target || !isUnread(target)) return;
      // Optimistic update.
      const readAt = new Date().toISOString();
      setItems((prev) =>
        prev.map((n) => (n.id === id ? { ...n, readAt } : n)),
      );
      decrementUnread(1);
      try {
        await markNotificationRead(apiCallRef.current, id);
      } catch {
        // Revert on failure and resync the badge.
        setItems((prev) =>
          prev.map((n) => (n.id === id ? { ...n, readAt: null } : n)),
        );
        void refreshUnreadCount();
      }
    },
    [items, decrementUnread, refreshUnreadCount],
  );

  const markAllRead = useCallback(async () => {
    const readAt = new Date().toISOString();
    const hadUnread = items.some(isUnread);
    if (!hadUnread) return;
    setItems((prev) =>
      prev.map((n) => (n.readAt ? n : { ...n, readAt })),
    );
    resetUnread();
    try {
      await markAllNotificationsRead(apiCallRef.current);
    } catch {
      void refresh();
      void refreshUnreadCount();
    }
  }, [items, resetUnread, refresh, refreshUnreadCount]);

  // Client-side type + timeframe filtering over the loaded pages. `typesKey`
  // keeps the memo stable even if the caller passes a fresh array each render.
  const typesKey = types.join(",");
  const filteredItems = useMemo(() => {
    const wanted = typesKey ? new Set(typesKey.split(",")) : null;
    const byType = wanted
      ? items.filter((n) => wanted.has(n.type))
      : items;
    return filterByTimeframe(byType, timeframe);
  }, [items, typesKey, timeframe]);

  return {
    items: filteredItems,
    isLoading,
    isLoadingMore,
    hasMore,
    error,
    loadMore,
    refresh,
    markRead,
    markAllRead,
  };
}
